import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MailIcon, PhoneIcon, MapPinIcon, CreditCardIcon, FileTextIcon, ActivityIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { Customer, PaymentMethod, CustomerTransaction } from '../types/Customer';
export function CustomerDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [activeTab, setActiveTab] = useState<'invoices' | 'cards' | 'transactions'>('invoices');
  const customer: Customer = {
    id: id || '1',
    name: 'Acme Corporation',
    email: 'billing@acme.com',
    phone: '+66 12 345 6789',
    address: '123 Business Street',
    city: 'Bangkok',
    country: 'Thailand',
    createdAt: '2023-06-15',
    totalSpent: 875000,
    invoiceCount: 12,
    status: 'active'
  };
  const paymentMethods: PaymentMethod[] = [{
    id: '1',
    type: 'visa',
    last4: '4242',
    expiryMonth: 12,
    expiryYear: 2025,
    cardholderName: 'John Doe',
    isDefault: true,
    addedAt: '2023-06-15'
  }, {
    id: '2',
    type: 'mastercard',
    last4: '5555',
    expiryMonth: 8,
    expiryYear: 2026,
    cardholderName: 'John Doe',
    isDefault: false,
    addedAt: '2023-09-20'
  }];
  const customerInvoices = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    date: '2024-01-01',
    amount: 125000,
    status: 'paid' as const,
    dueDate: '2024-01-15'
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-015',
    date: '2024-01-15',
    amount: 87500,
    status: 'sent' as const,
    dueDate: '2024-02-01'
  }, {
    id: '3',
    invoiceNumber: 'INV-2023-089',
    date: '2023-12-01',
    amount: 150000,
    status: 'paid' as const,
    dueDate: '2023-12-15'
  }];
  const transactions: CustomerTransaction[] = [{
    id: '1',
    date: '2024-01-10',
    description: 'Invoice Payment - INV-2024-001',
    amount: 125000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '1'
  }, {
    id: '2',
    date: '2023-12-10',
    description: 'Invoice Payment - INV-2023-089',
    amount: 150000,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    invoiceId: '3'
  }, {
    id: '3',
    date: '2023-11-15',
    description: 'Invoice Payment - INV-2023-078',
    amount: 95000,
    status: 'completed',
    paymentMethod: 'Mastercard •••• 5555'
  }, {
    id: '4',
    date: '2023-11-01',
    description: 'Refund - INV-2023-065',
    amount: -25000,
    status: 'refunded',
    paymentMethod: 'Visa •••• 4242'
  }];
  const history = [{
    id: 'h1',
    action: 'Invoice INV-2024-001 paid',
    date: '10 Jan 2024 at 14:32'
  }, {
    id: 'h2',
    action: 'Invoice INV-2024-015 sent',
    date: '15 Jan 2024 at 09:15'
  }, {
    id: 'h3',
    action: 'Payment method Visa •••• 4242 added',
    date: '15 Jun 2023 at 11:00'
  }, {
    id: 'h4',
    action: `${customer.name} was created`,
    date: '15 Jun 2023 at 10:30'
  }];
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      draft: 'bg-gray-100 text-gray-800 border border-gray-200',
      sent: 'bg-blue-50 text-blue-700 border border-blue-100',
      paid: 'bg-green-50 text-green-700 border border-green-100',
      overdue: 'bg-red-50 text-red-700 border border-red-100',
      completed: 'bg-green-50 text-green-700 border border-green-100',
      pending: 'bg-yellow-50 text-yellow-700 border border-yellow-100',
      failed: 'bg-red-50 text-red-700 border border-red-100',
      refunded: 'bg-gray-100 text-gray-800 border border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-7361">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-7362">
      {/* Header */}
      <div className="flex items-center" data-id="element-7363">
        <button onClick={() => navigate('/customers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-7364">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-7365" />
          All customers
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-7366">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-7367">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-7368">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-7369">
              <div className="flex justify-between items-start" data-id="element-7370">
                <div className="flex items-start gap-4" data-id="element-7371">
                  <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center text-[#F7941D] font-semibold text-xl flex-shrink-0" data-id="element-7372">
                    {customer.name.charAt(0)}
                  </div>
                  <div data-id="element-7373">
                    <h1 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-7374">
                      {customer.name}
                    </h1>
                    <div className="flex items-center text-sm" data-id="element-7375">
                      <span className="text-gray-500 mr-2" data-id="element-7376">Status:</span>
                      {getStatusBadge(customer.status)}
                    </div>
                  </div>
                </div>
                <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-7377">
                  Edit customer
                </button>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-7378">
              <div className="flex justify-between items-center mb-6" data-id="element-7379">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-7380">
                  Details
                </h3>
                <button onClick={() => navigate(`/customer-form?id=${customer.id}`)} className="text-sm font-medium text-[#F7941D] hover:text-[#D97B06]" data-id="element-7381">
                  Edit
                </button>
              </div>
              <div className="space-y-4" data-id="element-7382">
                <div className="grid grid-cols-3 gap-4" data-id="element-7383">
                  <div className="text-sm text-gray-500" data-id="element-7384">Email</div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-7385">
                    {customer.email}
                  </div>
                </div>
                {customer.phone && <div className="grid grid-cols-3 gap-4" data-id="element-7386">
                    <div className="text-sm text-gray-500" data-id="element-7387">Phone</div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-7388">
                      {customer.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-7389">
                  <div className="text-sm text-gray-500" data-id="element-7390">Address</div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-7391">
                    {customer.address}, {customer.city}, {customer.country}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-7392">
                  <div className="text-sm text-gray-500" data-id="element-7393">Customer since</div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-7394">
                    {new Date(customer.createdAt).toLocaleDateString('en-GB')}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-7395">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-7396">
                Summary
              </h3>
              <div className="grid grid-cols-3 gap-6" data-id="element-7397">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-7398">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-7399">Total Spent</div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-7400">
                    THB {customer.totalSpent.toLocaleString('en-US')}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-7401">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-7402">
                    Total Invoices
                  </div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-7403">
                    {customer.invoiceCount}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-7404">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-7405">
                    Avg. Invoice Value
                  </div>
                  <div className="text-lg font-semibold text-gray-900" data-id="element-7406">
                    THB{' '}
                    {Math.round(customer.totalSpent / customer.invoiceCount).toLocaleString('en-US')}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Methods Section */}
            <div className="p-8" data-id="element-7407">
              <div className="flex justify-between items-center mb-6" data-id="element-7408">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-7409">
                  Payment methods
                </h3>
                <button className="text-sm font-medium text-[#F7941D] hover:text-[#D97B06] flex items-center gap-1.5" data-id="element-7410">
                  <PlusIcon size={14} data-id="element-7411" />
                  Add card
                </button>
              </div>
              <div className="space-y-3" data-id="element-7412">
                {paymentMethods.map(method => <div key={method.id} className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-7413">
                    <div className="flex items-center justify-between" data-id="element-7414">
                      <div className="flex items-center gap-4" data-id="element-7415">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center" data-id="element-7416">
                          <CreditCardIcon size={20} className="text-[#F7941D]" data-id="element-7417" />
                        </div>
                        <div data-id="element-7418">
                          <div className="flex items-center gap-2" data-id="element-7419">
                            <span className="text-sm font-semibold text-gray-900" data-id="element-7420">
                              {method.type.charAt(0).toUpperCase() + method.type.slice(1)}{' '}
                              •••• {method.last4}
                            </span>
                            {method.isDefault && <span className="px-2 py-0.5 bg-[#F7941D]/10 text-[#F7941D] text-xs font-medium rounded-md border border-[#F7941D]/20" data-id="element-7421">
                                Default
                              </span>}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-7422">
                            Expires {method.expiryMonth}/{method.expiryYear} ·{' '}
                            {method.cardholderName}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-7423">
                        {!method.isDefault && <button className="px-3 py-1.5 text-xs font-medium text-[#F7941D] hover:bg-orange-50 rounded-lg transition-colors" data-id="element-7424">
                            Set as Default
                          </button>}
                        <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-7425">
                          <TrashIcon size={16} data-id="element-7426" />
                        </button>
                      </div>
                    </div>
                  </div>)}
                {paymentMethods.length === 0 && <div className="text-center py-8 text-gray-500 text-sm" data-id="element-7427">
                    No payment methods stored
                  </div>}
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-7428">
            <div className="flex space-x-6 px-8 border-b border-gray-100" data-id="element-7429">
              <button onClick={() => setActiveTab('invoices')} className={`pb-4 pt-5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'invoices' ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-7430">
                <FileTextIcon size={16} data-id="element-7431" />
                Invoices
              </button>
              <button onClick={() => setActiveTab('transactions')} className={`pb-4 pt-5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'transactions' ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-7432">
                <ActivityIcon size={16} data-id="element-7433" />
                Transactions
              </button>
            </div>

            {/* Invoices Tab */}
            {activeTab === 'invoices' && <div className="overflow-x-auto" data-id="element-7434">
                <table className="w-full text-left border-collapse" data-id="element-7435">
                  <thead data-id="element-7436">
                    <tr className="border-b border-gray-100" data-id="element-7437">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7438">
                        Invoice
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7439">
                        Date
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7440">
                        Due Date
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7441">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-7442">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-7443">
                    {customerInvoices.map(invoice => <tr key={invoice.id} onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-7444">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 group-hover:text-[#F7941D] transition-colors" data-id="element-7445">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-7446">
                          {new Date(invoice.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-7447">
                          {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-7448">
                          THB {invoice.amount.toLocaleString('en-US')}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-7449">
                          {getStatusBadge(invoice.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && <div className="overflow-x-auto" data-id="element-7450">
                <table className="w-full text-left border-collapse" data-id="element-7451">
                  <thead data-id="element-7452">
                    <tr className="border-b border-gray-100" data-id="element-7453">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7454">
                        Date
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7455">
                        Description
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7456">
                        Payment Method
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7457">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-7458">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-7459">
                    {transactions.map(transaction => <tr key={transaction.id} className="hover:bg-gray-50 transition-colors" data-id="element-7460">
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-7461">
                          {new Date(transaction.date).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-7462">
                          {transaction.description}
                          {transaction.invoiceId && <button onClick={e => {
                      e.stopPropagation();
                      navigate(`/invoice-detail/${transaction.invoiceId}`);
                    }} className="ml-2 text-[#F7941D] hover:text-[#D97B06] text-xs font-medium" data-id="element-7463">
                              View Invoice
                            </button>}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-7464">
                          {transaction.paymentMethod || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm" data-id="element-7465">
                          <span className={`font-medium ${transaction.amount < 0 ? 'text-red-600' : 'text-gray-900'}`} data-id="element-7466">
                            {transaction.amount < 0 ? '-' : ''}THB{' '}
                            {Math.abs(transaction.amount).toLocaleString('en-US')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-7467">
                          {getStatusBadge(transaction.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-7468">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-7469">
            History
          </h3>
          <div className="space-y-6" data-id="element-7470">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-7471">
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-7472"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-7473">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-7474" />
                </div>
                <div data-id="element-7475">
                  <p className="text-sm font-medium text-gray-900" data-id="element-7476">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-7477">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}